/**
 * Générateur — Encadrer et intercaler un décimal
 * Attendu ID : 608 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte_trou_drag, relier, tableau_vf
 *
 * Règles dures de la fiche cluster 5 (section ID 608) :
 *   - Encadrement : bornes au dixième ou au centième UNIQUEMENT
 *     (millième hors programme pour l'encadrement — fiche ligne 339).
 *   - Intercalation : les décimaux à 3+ décimales SONT AU PROGRAMME
 *     (densité des décimaux, cœur conceptuel de l'attendu).
 *   - Règle d'exclusion : le nombre à encadrer n'est jamais trivialement
 *     égal à l'une des bornes de son rang cible.
 *   - Paires très proches (ex : 5,1 et 5,11) obligatoires pour tester
 *     la densité — cas-clé central de l'attendu.
 *   - Le mot "strictement" doit apparaître dans les consignes
 *     d'intercalation (ligne 447).
 *
 * Mécanismes-clés testés :
 *   - Encadrement au dixième (rang dominant, axe 1 ≥ 3 ex/10)
 *   - Encadrement au centième
 *   - Propagation de retenue (9,95 → 9,9 < 9,95 < 10,0)
 *   - Densité / intercalation dans paires très proches
 *   - Pièges : saut de rang, inversion, mauvais rang, confusion grandeur/chiffres
 *
 * Variantes estimées : >3000
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier, makeTexteTrou, makeClasser,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 608;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Rangs utilisés pour l'encadrement (AU PROGRAMME — millième exclu). */
const RANGS_ENCADRE = [
    { key: 'dixieme', libelle: 'au dixième', decimals: 1 },
    { key: 'centieme', libelle: 'au centième', decimals: 2 },
];

// ─── Labels ───
const L_QCM = 'Choisir le bon encadrement';
const L_QCM_MULTI = 'Identifier les décimaux intercalés entre deux bornes proches';
const L_RELIER = 'Placer chaque décimal dans le couple qui l\'encadre (contrainte)';
const L_VF = 'Vrai/Faux sur encadrement et intercalation de décimaux';
const L_TROU = 'Compléter un encadrement';

// ─── Helpers numériques ───

/** Arrondi IEEE-safe (pour toFixed exact). */
const roundTo = (n, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.round((n + Number.EPSILON) * factor) / factor;
};

/** Formate un nombre en français avec EXACTEMENT n décimales. */
const toFrFixed = (n, decimals) => {
    const rounded = roundTo(n, decimals);
    if (decimals === 0) return String(Math.round(rounded + Number.EPSILON));
    return rounded.toFixed(decimals).replace('.', ',');
};

/** Formate un nombre source en français avec son nombre naturel de décimales. */
const toFr = (n, sourceDecimals) => n.toFixed(sourceDecimals).replace('.', ',');

/**
 * Encadre n au rang donné (dixième ou centième).
 * Retourne {inf, sup} : les deux bornes consécutives à ce rang, avec
 * propagation de retenue gérée naturellement (9,95 → 9,9 < 9,95 < 10,0).
 */
const encadreAt = (n, rangDecimals) => {
    const factor = Math.pow(10, rangDecimals);
    const scaled = n * factor;
    const kInf = Math.floor(scaled + 1e-9);
    const inf = kInf / factor;
    const sup = (kInf + 1) / factor;
    return { inf, sup };
};

/**
 * Génère un décimal source à 2 ou 3 décimales, non trivial au rang cible.
 * Respecte la règle d'exclusion : n ≠ borne inf et n ≠ borne sup.
 *
 * Si forcePropagation est true, on force un nombre dont la borne sup
 * enjambe la partie entière (ex : 9,95 au dixième → 9,9 < ... < 10,0).
 */
const randSourceEncadre = (rangDecimals, { forcePropagation = false, intMin = 0, intMax = 20 } = {}) => {
    for (let tries = 0; tries < 80; tries++) {
        const sourceDecimals = randomChoice([2, 3]);
        const factor = Math.pow(10, sourceDecimals);
        let intPart, fracRaw;
        if (forcePropagation) {
            // Au dixième : 9,95..9,99 (inf=9,9, sup=10,0)
            // Au centième : 9,995..9,999 (inf=9,99, sup=10,00)
            intPart = 9;
            if (rangDecimals === 1) {
                // On veut une borne sup qui enjambe → n ≥ 9,9
                // avec sourceDecimals = 2 : 95..99 ; avec 3 : 900..999
                if (sourceDecimals === 2) fracRaw = randInt(95, 99);
                else fracRaw = randInt(900, 999);
            } else {
                // centième propagation : n ≥ 9,99
                if (sourceDecimals === 2) { fracRaw = 99; }
                else fracRaw = randInt(991, 999);
            }
        } else {
            intPart = randInt(intMin, intMax);
            fracRaw = randInt(1, factor - 1);
        }

        // Éviter trailing zero (nombre plus naturel à l'affichage)
        if (sourceDecimals >= 2 && fracRaw % 10 === 0) continue;

        const n = intPart + fracRaw / factor;
        const { inf, sup } = encadreAt(n, rangDecimals);
        // Règle d'exclusion : n ≠ inf et n ≠ sup
        if (Math.abs(n - inf) < 1e-9) continue;
        if (Math.abs(n - sup) < 1e-9) continue;

        return { n, sourceDecimals, inf, sup };
    }
    // Fallback déterministe
    if (rangDecimals === 1) {
        return { n: 2.375, sourceDecimals: 3, inf: 2.3, sup: 2.4 };
    }
    return { n: 1.234, sourceDecimals: 3, inf: 1.23, sup: 1.24 };
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Choisir l'encadrement correct au dixième/centième
// ═══════════════════════════════════════════════════════════

const genEncadrerQcm = () => {
    // Rang dixième dominant (70%) pour quota axe 1 de la fiche
    const rang = Math.random() < 0.7 ? RANGS_ENCADRE[0] : RANGS_ENCADRE[1];
    const { n, sourceDecimals, inf, sup } = randSourceEncadre(rang.decimals);

    const nStr = toFr(n, sourceDecimals);
    const infStr = toFrFixed(inf, rang.decimals);
    const supStr = toFrFixed(sup, rang.decimals);

    const correct = `${infStr} < ${nStr} < ${supStr}`;

    // Pièges — 4 familles, on en TIRE 3 pour la variété (l'inversion, éliminable
    // par un élève attentif, n'apparaît plus systématiquement) :
    //   (a) saut de rang, (b) inversion, (c) rang trop grossier voisin,
    //   (d) rang trop FIN : encadrement VRAI mais plus précis que demandé
    //       (ex. 5,63 < 5,64 < 5,65 pour une cible au dixième) — le miroir de (c).
    //       Pour la cible centième, le rang fin serait le millième (hors
    //       programme, fiche cluster 5) → on utilise l'unité à la place.
    const supPlusOne = toFrFixed(sup + 1 / Math.pow(10, rang.decimals), rang.decimals);
    const intFloor = Math.floor(n);
    const rangUnite = `${intFloor} < ${nStr} < ${intFloor + 1}`;
    let rangVoisin;
    let rangQuatrieme;
    if (rang.decimals === 1) {
        // Cible dixième : (c) = unité, (d) = centième
        rangVoisin = rangUnite;
        const epsFin = 1 / 100;
        let dFin = encadreAt(n, 2);
        // n exactement au centième → encadrement dégénéré (inf = n) → bornes n ± 0,01
        if (Math.abs(dFin.inf - n) < 1e-9) dFin = { inf: roundTo(n - epsFin, 2), sup: roundTo(n + epsFin, 2) };
        rangQuatrieme = `${toFrFixed(dFin.inf, 2)} < ${nStr} < ${toFrFixed(dFin.sup, 2)}`;
    } else {
        // Cible centième : (c) = dixième, (d) = unité
        const d = encadreAt(n, 1);
        rangVoisin = `${toFrFixed(d.inf, 1)} < ${nStr} < ${toFrFixed(d.sup, 1)}`;
        rangQuatrieme = rangUnite;
    }
    // Le saut de rang (a) est le piège le PLUS discriminant (encadrement vrai,
    // bornes au bon rang, mais un dixième sauté) → GARANTI à chaque tirage.
    // Les 2 places restantes tournent entre (b), (c), (d) — l'inversion,
    // éliminable d'un coup d'œil, n'apparaît donc que 2 tirages sur 3.
    const piegeSaut = `${infStr} < ${nStr} < ${supPlusOne}`;
    const tournants = [...new Set([
        `${supStr} < ${nStr} < ${infStr}`,        // (b) inversion des bornes
        rangVoisin,                                // (c) rang trop grossier voisin
        rangQuatrieme,                             // (d) rang trop fin (ou unité)
    ])].filter(f => f !== correct && f !== piegeSaut);

    let distArr = [
        ...(piegeSaut !== correct ? [piegeSaut] : []),
        ...randomChoiceMultiple(tournants, Math.min(2, tournants.length)),
    ];
    // Fallback : borne inf décalée d'un rang si moins de 3 distracteurs
    if (distArr.length < 3) {
        const infMinusOne = toFrFixed(inf - 1 / Math.pow(10, rang.decimals), rang.decimals);
        const fb = `${infMinusOne} < ${nStr} < ${supStr}`;
        if (fb !== correct && !distArr.includes(fb)) distArr.push(fb);
    }
    distArr = distArr.slice(0, 3);
    const choix = [correct, ...distArr];

    return makeQcm(
        choix,
        correct,
        `<p>Parmi les propositions suivantes, choisis l'encadrement ${rang.libelle} correct de ${nStr}.</p>`,
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Décimaux strictement intercalés (paire très proche)
// ═══════════════════════════════════════════════════════════

/**
 * Paires très proches canoniques pour tester la densité.
 * Format : {a, b, aDecs, bDecs} — a et b sont les bornes numériques,
 * aDecs/bDecs leur nombre naturel de décimales pour l'affichage.
 */
const PAIRES_TRES_PROCHES = [
    { a: 5.1, b: 5.11, aDecs: 1, bDecs: 2 },
    { a: 3.45, b: 3.48, aDecs: 2, bDecs: 2 }, // paire centième proche
    { a: 7.2, b: 7.21, aDecs: 1, bDecs: 2 },
    { a: 2.9, b: 2.91, aDecs: 1, bDecs: 2 },
    { a: 0.4, b: 0.41, aDecs: 1, bDecs: 2 },
    { a: 6.3, b: 6.31, aDecs: 1, bDecs: 2 },
];

const genEncadrerQcmMultiple = () => {
    // Clone pour éviter la mutation de PAIRES_TRES_PROCHES (le fallback ré-assigne paire.a etc.)
    const paire = { ...randomChoice(PAIRES_TRES_PROCHES) };
    const { a, b, aDecs, bDecs } = paire;

    const aStr = toFr(a, aDecs);
    const bStr = toFr(b, bDecs);

    // Précision nécessaire pour intercaler : b a plus de rangs que a
    // → on doit prendre des décimaux à plus de décimales (densité)
    // Ex : 5,1 et 5,11 → besoin de 5,105 / 5,108 / 5,102 (3 décs)

    // Bonnes réponses : 3 décimaux strictement entre a et b avec ≥ 3 décimales
    const correct = [];
    const tryAddCorrect = (val, decs) => {
        if (val > a + 1e-12 && val < b - 1e-12) {
            const s = toFr(val, decs);
            if (!correct.includes(s)) correct.push(s);
        }
    };

    // Paire centième proche (ex 3,45 et 3,48) : on peut intercaler 3,46 / 3,47 (2 décs)
    // ET aussi 3,465 (3 décs, densité)
    if (bDecs === 2 && aDecs === 2) {
        // ex 3,45 et 3,48 → on utilise des décimaux à 3 décs pour densité
        // et d'autres à 2 décs pour varier
        const midCents = [];
        const start = Math.round(a * 100);
        const end = Math.round(b * 100);
        for (let k = start + 1; k < end; k++) {
            midCents.push(k / 100);
        }
        // Choisir 1 au centième + 2 au millième
        if (midCents.length > 0) {
            tryAddCorrect(midCents[0], 2);
        }
        // Au millième
        const startM = Math.round(a * 1000);
        const endM = Math.round(b * 1000);
        const millemes = [];
        for (let k = startM + 1; k < endM; k++) {
            if (k % 10 !== 0) millemes.push(k / 1000);
        }
        const chosenM = shuffle(millemes).slice(0, 2);
        chosenM.forEach((v) => tryAddCorrect(v, 3));
    } else {
        // Paire dixième/centième (ex 5,1 et 5,11) → forcément densité à 3 décs
        const startM = Math.round(a * 1000);
        const endM = Math.round(b * 1000);
        const millemes = [];
        for (let k = startM + 1; k < endM; k++) {
            if (k % 10 !== 0) millemes.push(k / 1000);
        }
        const chosenM = shuffle(millemes).slice(0, 3);
        chosenM.forEach((v) => tryAddCorrect(v, 3));
    }

    // Garantir au moins 3 bonnes : fallback déterministe
    if (correct.length < 3) {
        // Fallback 5,1 / 5,11
        correct.length = 0;
        correct.push('5,102', '5,105', '5,108');
        paire.a = 5.1; paire.b = 5.11; paire.aDecs = 1; paire.bDecs = 2;
    }
    const correctFinal = correct.slice(0, 3);

    // Pièges — 3 familles distinctes (spec fiche ligne 448)
    const pieges = [];

    // (1) Borne elle-même (test du strict) : b
    pieges.push(toFr(paire.b, paire.bDecs));

    // (2) Extérieur visuellement plausible : b + ε (ex 5,111 quand b=5,11)
    // Ou "b suivi d'un chiffre" : pour 5,11 → 5,111 (> 5,11)
    const bPlus = paire.b + 1 / Math.pow(10, paire.bDecs + 1);
    pieges.push(toFr(bPlus, paire.bDecs + 1));

    // (3) Extérieur net : un nombre clairement < a (ex 4,9 quand a=5,1)
    // ou > b+0,1 : on prend intPart(a) + (frac - 0.2) si possible
    const intA = Math.floor(paire.a);
    const extNet = intA - 0.1 < 0 ? paire.b + 0.5 : intA - 0.1;
    pieges.push(toFr(extNet, 1));

    // Dédoublonner les pièges
    const uniquePieges = [];
    for (const p of pieges) {
        if (!uniquePieges.includes(p) && !correctFinal.includes(p)) uniquePieges.push(p);
    }
    // Compléter si besoin
    while (uniquePieges.length < 3) {
        const alt = toFr(paire.a - 0.01, 2);
        if (!uniquePieges.includes(alt) && !correctFinal.includes(alt)) uniquePieges.push(alt);
        else break;
    }

    const pigesFinal = uniquePieges.slice(0, 3);
    const allItems = [...correctFinal, ...pigesFinal];

    return makeQcm(
        allItems,
        correctFinal,
        `<p>Parmi ces décimaux, lesquels se trouvent <strong>strictement</strong> entre ${toFr(paire.a, paire.aDecs)} et ${toFr(paire.b, paire.bDecs)} ?</p>`,
        opts(L_QCM_MULTI),
        true,
    );
};

// ═══════════════════════════════════════════════════════════
// 3. RELIER — Contrainte de satisfaction avec couples imbriqués
// ═══════════════════════════════════════════════════════════
//
// Principe pédagogique : 2 zones disjointes, chacune avec un couple "large"
// (dixième) et un couple "étroit" (centième) inclus dans le large. Chaque
// zone fournit 2 candidats :
//   - un décimal "uniquement dans le large" (ex: 5,43 pour [5,4 ; 5,5])
//   - un décimal "dans les deux" (ex: 5,415 pour [5,41 ; 5,42] ⊂ [5,4 ; 5,5])
//
// L'élève ne peut pas deviner par simple comparaison de chiffres : il doit
// raisonner par élimination — le candidat "ambigu" est forcé dans l'étroit
// parce que l'autre candidat ne peut aller QUE dans le large.

const genEncadrerRelier = () => {
    /**
     * Construit une zone : 1 couple large au dixième + 1 couple étroit au centième
     * inclus dedans, + 2 candidats (onlyWide, both).
     */
    const makeZone = (intPart) => {
        // Dixième de départ dans [1, 8] pour laisser de la marge
        const dStart = randInt(1, 8);
        const wideInf = roundTo(intPart + dStart / 10, 1);
        const wideSup = roundTo(intPart + (dStart + 1) / 10, 1);

        // Narrow : [wideInf + c/100 ; wideInf + (c+1)/100] avec c ∈ [1, 8]
        const c = randInt(1, 8);
        const narrowInf = roundTo(wideInf + c / 100, 2);
        const narrowSup = roundTo(wideInf + (c + 1) / 100, 2);

        // onlyWide : un centième dans le wide mais hors du narrow
        // Les centièmes possibles sont 1..9 sauf c et c+1
        const availableCents = [];
        for (let i = 1; i < 10; i++) {
            if (i !== c && i !== c + 1) availableCents.push(i);
        }
        const onlyWideC = randomChoice(availableCents);
        const onlyWide = roundTo(wideInf + onlyWideC / 100, 2);

        // both : un millième strictement à l'intérieur du narrow
        // narrow = [wideInf + c/100 ; wideInf + (c+1)/100]
        // → millième valide : wideInf + c/100 + m/1000 avec m ∈ [1, 9]
        const m = randInt(1, 9);
        const both = roundTo(wideInf + c / 100 + m / 1000, 3);

        return { wideInf, wideSup, narrowInf, narrowSup, onlyWide, both };
    };

    // Deux zones avec parties entières distinctes (pas de contamination croisée)
    const int1 = randInt(1, 15);
    let int2 = randInt(1, 15);
    while (int2 === int1) int2 = randInt(1, 15);

    const z1 = makeZone(int1);
    const z2 = makeZone(int2);

    const pairs = [
        { source: toFr(z1.onlyWide, 2), target: `${toFrFixed(z1.wideInf, 1)} et ${toFrFixed(z1.wideSup, 1)}` },
        { source: toFr(z1.both, 3),     target: `${toFrFixed(z1.narrowInf, 2)} et ${toFrFixed(z1.narrowSup, 2)}` },
        { source: toFr(z2.onlyWide, 2), target: `${toFrFixed(z2.wideInf, 1)} et ${toFrFixed(z2.wideSup, 1)}` },
        { source: toFr(z2.both, 3),     target: `${toFrFixed(z2.narrowInf, 2)} et ${toFrFixed(z2.narrowSup, 2)}` },
    ];

    return makeRelier(
        pairs,
        `<p>Place chaque nombre dans le couple qui l'encadre. Attention : chaque nombre est utilisé une seule fois et chaque couple ne reçoit qu'un seul nombre.</p>`,
        opts(L_RELIER),
    );
};

// ═══════════════════════════════════════════════════════════
// 4. VRAI/FAUX — 6 lignes 3V/3F, ≥1 ligne densité obligatoire
// ═══════════════════════════════════════════════════════════

const genEncadrerVF = () => {
    const lignes = [];
    const bonnes = []; // true = Vrai, false = Faux
    const seen = new Set();

    const tryPush = (text, isVrai) => {
        if (seen.has(text)) return false;
        seen.add(text);
        lignes.push(text);
        bonnes.push(isVrai);
        return true;
    };

    // L1 OBLIGATOIRE — Ligne DENSITÉ négation (FAUX) — cas-clé ligne 470 fiche
    const paireDensite = randomChoice([
        { a: 5.1, b: 5.11, aDecs: 1, bDecs: 2 },
        { a: 2.9, b: 2.91, aDecs: 1, bDecs: 2 },
        { a: 7.2, b: 7.21, aDecs: 1, bDecs: 2 },
    ]);
    tryPush(
        `Aucun décimal ne peut s'intercaler entre ${toFr(paireDensite.a, paireDensite.aDecs)} et ${toFr(paireDensite.b, paireDensite.bDecs)}`,
        false,
    );

    // L2 — Intercalation VALIDE au centième (VRAI)
    // ex : 3,47 s'intercale strictement entre 3,45 et 3,48
    {
        const a = 3.45, b = 3.48, mid = 3.47;
        tryPush(
            `On peut intercaler ${toFr(mid, 2)} strictement entre ${toFr(a, 2)} et ${toFr(b, 2)}`,
            true,
        );
    }

    // L3 — Encadrement au dixième VALIDE (VRAI) avec nombre 3 décimales
    {
        const candidates = [
            { n: 2.375, sourceDecimals: 3 },
            { n: 7.823, sourceDecimals: 3 },
            { n: 4.152, sourceDecimals: 3 },
            { n: 6.478, sourceDecimals: 3 },
        ];
        const c = randomChoice(candidates);
        const { inf, sup } = encadreAt(c.n, 1);
        tryPush(
            `${toFr(c.n, c.sourceDecimals)} est compris entre ${toFrFixed(inf, 1)} et ${toFrFixed(sup, 1)} au dixième`,
            true,
        );
    }

    // L4 — Piège "le dixième qui suit 9,9 est 9,10" (FAUX, propagation)
    {
        const pairs = [
            'Le dixième qui suit 9,9 est 9,10',
            'Le dixième qui suit 4,9 est 4,10',
            'Le dixième qui suit 7,9 est 7,10',
        ];
        tryPush(randomChoice(pairs), false);
    }

    // L5 — Confusion grandeur/chiffres (FAUX) — ex : 6,10 entre 6,6 et 6,9
    {
        const conf = [
            { x: '6,10', a: '6,6', b: '6,9' },
            { x: '4,10', a: '4,5', b: '4,8' },
            { x: '2,10', a: '2,3', b: '2,7' },
            { x: '8,10', a: '8,4', b: '8,7' },
        ];
        const c = randomChoice(conf);
        tryPush(
            `${c.x} est strictement compris entre ${c.a} et ${c.b}`,
            false,
        );
    }

    // L6 — Intercalation au centième VALIDE (VRAI) pour équilibre 3V/3F
    {
        const candidates = [
            { a: 1.45, b: 1.48, mid: 1.46 },
            { a: 4.23, b: 4.27, mid: 4.25 },
            { a: 9.51, b: 9.54, mid: 9.52 },
        ];
        const c = randomChoice(candidates);
        tryPush(
            `${toFr(c.mid, 2)} est strictement compris entre ${toFr(c.a, 2)} et ${toFr(c.b, 2)}`,
            true,
        );
    }

    // Sécurité : équilibrage 3V/3F (correction finale si deduplication a réduit)
    while (lignes.length < 6) {
        const currentVrai = bonnes.filter((b) => b).length;
        const currentFaux = bonnes.filter((b) => !b).length;
        if (currentFaux < 3) {
            tryPush(`${lignes.length},0 est strictement compris entre 0 et 1`, false);
        } else if (currentVrai < 3) {
            tryPush(`0,${lignes.length + 1} est strictement compris entre 0 et 1`, true);
        } else break;
    }

    // SHUFFLE les lignes en conservant la cohérence booléenne
    const indexed = lignes.map((l, i) => ({ l, v: bonnes[i] }));
    const shuffled = shuffle(indexed);
    const finalLignes = shuffled.map((x) => x.l);
    const finalBonnes = shuffled.map((x) => (x.v ? 0 : 1));

    return makeVraiFaux(
        finalLignes,
        finalBonnes,
        `<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. TEXTE À TROUS (DRAG) — 2 encadrements × 2 bornes = 4 trous
// ═══════════════════════════════════════════════════════════

const genEncadrerTrouDrag = () => {
    // Rang dixième uniquement pour format cohérent (axe 1 quota)
    const rang = RANGS_ENCADRE[0];

    // n1 : cas standard ; n2 : cas propagation (9,XX)
    const n1Data = randSourceEncadre(rang.decimals);
    const n2Data = randSourceEncadre(rang.decimals, { forcePropagation: true });

    const n1 = toFr(n1Data.n, n1Data.sourceDecimals);
    const inf1 = toFrFixed(n1Data.inf, rang.decimals);
    const sup1 = toFrFixed(n1Data.sup, rang.decimals);
    const n2 = toFr(n2Data.n, n2Data.sourceDecimals);
    const inf2 = toFrFixed(n2Data.inf, rang.decimals);
    const sup2 = toFrFixed(n2Data.sup, rang.decimals);

    const bonnes = [inf1, sup1, inf2, sup2];

    // Distinction des 4 bonnes : si collision, fallback déterministe
    if (new Set(bonnes).size < 4) {
        return buildTrouDragPayload(
            { n: '2,375', inf: '2,3', sup: '2,4' },
            { n: '9,95', inf: '9,9', sup: '10,0' },
        );
    }

    return buildTrouDragPayload(
        { n: n1, inf: inf1, sup: sup1 },
        { n: n2, inf: inf2, sup: sup2 },
    );
};

const buildTrouDragPayload = (d1, d2) => {
    const bonnes = [d1.inf, d1.sup, d2.inf, d2.sup];

    const phrase = `L'encadrement au dixième de ${d1.n} est {{${d1.inf}}} < ${d1.n} < {{${d1.sup}}}. L'encadrement au dixième de ${d2.n} est {{${d2.inf}}} < ${d2.n} < {{${d2.sup}}}.`;

    // Distracteurs — 4 familles de pièges, FILTRÉS NUMÉRIQUEMENT contre les
    // bornes attendues (arbitrage 2026-07-04) : « 2,9 < 2,95 < 3 » est
    // mathématiquement JUSTE (3 = 3,0), donc une étiquette numériquement égale
    // à une borne serait un piège injuste. On tire 3 familles parmi 4 pour la
    // variété, complétées par des valeurs sûres si le filtre a réduit la liste.
    const parseFr = (s) => parseFloat(s.replace(',', '.'));
    const fmt1 = (v) => v.toFixed(1).replace('.', ',');
    // (d) « Encadrement au plus près » : dernier chiffre de n1 incrémenté
    // (ex 11,783 pour 11,782) — vrai encadrement, mais PAS au dixième
    const decsN1 = (d1.n.split(',')[1] || '').length;
    const presDeN1 = (parseFr(d1.n) + Math.pow(10, -decsN1)).toFixed(decsN1).replace('.', ',');
    const pieges = filtrerDistracteurs(phrase, [
        // (a) Saut de dixième : sup1 + 0,1 (ex 2,5 au lieu de 2,4)
        fmt1(parseFr(d1.sup) + 0.1),
        // (b) Confusion grandeur/chiffres : "X,10" (ex 9,10 au lieu de 10,0)
        `${Math.floor(parseFr(d2.n))},10`,
        // (c) Mauvais rang : partie entière du nombre n1 (ex "2" quand n1=2,375)
        String(Math.floor(parseFr(d1.n))),
        // (d) Encadrement au plus près (mauvais rang, dernier chiffre +1)
        presDeN1,
    ]);
    const complements = filtrerDistracteurs(phrase, [
        fmt1(parseFr(d1.inf) - 0.1),
        fmt1(parseFr(d2.sup) + 0.1),
        String(Math.floor(parseFr(d2.n)) + 2),
    ]).filter(c => !pieges.includes(c));
    const distArr = [
        ...randomChoiceMultiple(pieges, Math.min(3, pieges.length)),
        ...complements,
    ].slice(0, 3);

    return makeTexteTrou(
        phrase,
        `<p>Complète en faisant glisser les bonnes valeurs dans les cases.</p>`,
        { ...opts(L_TROU), mode: 'drag', distracteurs: distArr },
    );
};

// ═══════════════════════════════════════════════════════════
// 6. CONTEXTE RÉEL — Sélection par contraintes (QCM / V/F / Classer)
// ═══════════════════════════════════════════════════════════
//
// Template pédagogique inspiré des manuels : on donne un contexte concret
// (casting, mission, concours...), une liste de critères (encadrements +
// inégalité simple) et un tableau de candidats. L'élève doit vérifier
// chaque candidat contre chaque critère.
//
// 3 formats partagent le MÊME helper central `genContexteSelection` et le
// même pool de données. Seul le format de restitution change.

const L_CONTEXTE = 'Sélection par contraintes dans un contexte réel';

// Contextes : intro + phrase de conclusion + types compatibles.
// `{P}` sera remplacé par le type choisi parmi `types`.
const CONTEXTES = [
    {
        intro: 'Pour un film, on cherche un {P} qui',
        conclusion: 'Quel {P} est choisi pour le film ?',
        types: ['pingouin', 'robot', 'chat', 'enfant'],
    },
    {
        intro: 'Pour une mission spatiale, on sélectionne un {P} qui',
        conclusion: 'Quel {P} part en mission ?',
        types: ['robot', 'pingouin'],
    },
    {
        intro: 'Pour un concours de saut, on recrute un {P} qui',
        conclusion: 'Quel {P} est retenu au concours ?',
        types: ['pingouin', 'chat', 'enfant', 'robot'],
    },
    {
        intro: 'Pour une expédition polaire, on cherche un {P} qui',
        conclusion: 'Quel {P} accompagne l\'expédition ?',
        types: ['pingouin', 'robot'],
    },
    {
        intro: 'Pour une émission de télé, on sélectionne un {P} qui',
        conclusion: 'Quel {P} participe à l\'émission ?',
        types: ['pingouin', 'robot', 'chat', 'enfant'],
    },
    {
        intro: 'Pour une publicité, on cherche un {P} qui',
        conclusion: 'Quel {P} est retenu pour la publicité ?',
        types: ['pingouin', 'robot', 'chat', 'enfant'],
    },
    {
        intro: 'Pour une équipe de foot junior, on sélectionne un {P} qui',
        conclusion: 'Quel {P} rejoint l\'équipe ?',
        types: ['enfant'],
    },
    {
        intro: 'Pour un laboratoire, on recrute un {P} qui',
        conclusion: 'Quel {P} rejoint le laboratoire ?',
        types: ['robot'],
    },
];

// Types de personnages avec pools de noms
const TYPES_PERSO = [
    { singulier: 'pingouin', pool: ['Fluffy', 'Pitch', 'Melman', 'Pibouli', 'Hugsy', 'Rico', 'Kowalski', 'Skipper'] },
    { singulier: 'robot',    pool: ['R2', 'Bolt', 'Zorg', 'Pixie', 'Mek', 'Nano', 'Axo', 'Bibop'] },
    { singulier: 'chat',     pool: ['Minou', 'Félix', 'Léo', 'Moka', 'Simba', 'Pepper', 'Tigre', 'Nala'] },
    { singulier: 'enfant',   pool: ['Léa', 'Tom', 'Zoé', 'Lou', 'Max', 'Jade', 'Noa', 'Inès'] },
];

// Attributs : type de contrainte, unité, précision, plage globale, phrase
// `type` : 'encadrement' | 'inferieur' | 'superieur'
const ATTRIBUTS = [
    {
        key: 'Taille', unit: 'm', decimals: 2, range: [0.60, 1.20], type: 'encadrement',
        phrase: (b) => `mesure entre ${toFrFixed(b.min, 2)} m et ${toFrFixed(b.max, 2)} m`,
    },
    {
        key: 'Poids', unit: 'kg', decimals: 1, range: [3, 15], type: 'encadrement',
        phrase: (b) => `pèse entre ${toFrFixed(b.min, 1)} kg et ${toFrFixed(b.max, 1)} kg`,
    },
    {
        key: 'Saut', unit: 'm', decimals: 2, range: [1.00, 3.50], type: 'encadrement',
        phrase: (b) => `saute entre ${toFrFixed(b.min, 2)} m et ${toFrFixed(b.max, 2)} m`,
    },
    {
        key: 'Score', unit: 'pts', decimals: 0, range: [50, 200], type: 'encadrement',
        phrase: (b) => `a un score entre ${b.min} pts et ${b.max} pts`,
    },
    {
        key: 'Âge', unit: 'ans', decimals: 0, range: [5, 15], type: 'inferieur',
        phrase: (b) => `a moins de ${b.seuil} ans`,
    },
    {
        key: 'Vitesse', unit: 'km/h', decimals: 0, range: [10, 30], type: 'superieur',
        phrase: (b) => `court à plus de ${b.seuil} km/h`,
    },
];

// ─── Helpers numériques contexte ───

/** Tire un nombre aléatoire dans [a, b] aligné sur la précision demandée. */
const randInRange = (a, b, decimals) => {
    const factor = Math.pow(10, decimals);
    const iMin = Math.ceil(a * factor);
    const iMax = Math.floor(b * factor);
    return (iMin + Math.floor(Math.random() * (iMax - iMin + 1))) / factor;
};

const EPS = (d) => 1 / Math.pow(10, d);

/** Génère une valeur qui respecte (valid=true) ou viole (valid=false) un critère. */
const genValeurCritere = (crit, valid) => {
    const { type, decimals, range, bornes } = crit;
    const [rMin, rMax] = range;
    const eps = EPS(decimals);

    if (type === 'encadrement') {
        if (valid) return roundTo(randInRange(bornes.min, bornes.max, decimals), decimals);
        // Invalide : soit sous bornes.min, soit au-dessus de bornes.max
        const canLow = bornes.min - eps >= rMin;
        const canHigh = bornes.max + eps <= rMax;
        if (canLow && canHigh) {
            return Math.random() < 0.5
                ? roundTo(randInRange(rMin, bornes.min - eps, decimals), decimals)
                : roundTo(randInRange(bornes.max + eps, rMax, decimals), decimals);
        }
        if (canLow)  return roundTo(randInRange(rMin, bornes.min - eps, decimals), decimals);
        return roundTo(randInRange(bornes.max + eps, rMax, decimals), decimals);
    }
    if (type === 'inferieur') {
        // Valide si valeur < seuil
        if (valid) return roundTo(randInRange(rMin, bornes.seuil - eps, decimals), decimals);
        return roundTo(randInRange(bornes.seuil, rMax, decimals), decimals);
    }
    // superieur : valide si valeur > seuil
    if (valid) return roundTo(randInRange(bornes.seuil + eps, rMax, decimals), decimals);
    return roundTo(randInRange(rMin, bornes.seuil, decimals), decimals);
};

/**
 * Helper central : génère la structure de base d'un exercice de sélection.
 * @param {{nbCandidats?: number, nbValides?: number}} options
 * @returns {{intro, conclusion, type, criteres, candidats}}
 */
const genContexteSelection = ({ nbCandidats = 6, nbValides = 1 } = {}) => {
    const contexte = randomChoice(CONTEXTES);
    // Type choisi parmi ceux compatibles avec le contexte
    const typeName = randomChoice(contexte.types);
    const type = TYPES_PERSO.find((t) => t.singulier === typeName);
    const intro = contexte.intro.replace(/\{P\}/g, type.singulier);
    const conclusion = contexte.conclusion.replace(/\{P\}/g, type.singulier);

    // 3 attributs : 2 encadrements distincts + 1 inégalité (inferieur/superieur)
    const encadrements = ATTRIBUTS.filter((a) => a.type === 'encadrement');
    const inegalites   = ATTRIBUTS.filter((a) => a.type !== 'encadrement');
    const selectedEnc = randomChoiceMultiple(encadrements, 2);
    const selectedIne = randomChoice(inegalites);
    const selectedAttrs = shuffle([...selectedEnc, selectedIne]);

    // Générer bornes/seuils dans un sous-intervalle du range
    const criteres = selectedAttrs.map((attr) => {
        const [rMin, rMax] = attr.range;
        const span = rMax - rMin;
        if (attr.type === 'encadrement') {
            // Bornes internes au range, avec un écart de ~15-30% du span
            const ecart = roundTo(span * (0.10 + Math.random() * 0.15), attr.decimals);
            const min = roundTo(rMin + span * (0.15 + Math.random() * 0.40), attr.decimals);
            const max = roundTo(min + ecart, attr.decimals);
            return { ...attr, bornes: { min, max } };
        }
        // inégalité : seuil au milieu du range
        const seuil = roundTo(rMin + span * (0.30 + Math.random() * 0.40), attr.decimals);
        return { ...attr, bornes: { seuil } };
    });

    // Tirer les noms
    const noms = randomChoiceMultiple(type.pool, nbCandidats);

    // Répartir les candidats valides / non-valides (répartition mélangée)
    const validFlags = shuffle([
        ...new Array(nbValides).fill(true),
        ...new Array(nbCandidats - nbValides).fill(false),
    ]);

    const candidats = noms.map((nom, i) => {
        const isValid = validFlags[i];
        const valeurs = {};
        if (isValid) {
            criteres.forEach((c) => { valeurs[c.key] = genValeurCritere(c, true); });
        } else {
            // Choisir 1 critère à violer (les autres restent respectés)
            const violatedIdx = randInt(0, criteres.length - 1);
            criteres.forEach((c, idx) => {
                valeurs[c.key] = genValeurCritere(c, idx !== violatedIdx);
            });
        }
        return { nom, valid: isValid, valeurs };
    });

    return { intro, conclusion, type, criteres, candidats };
};

/** Construit le HTML de l'énoncé : intro + critères + tableau de candidats. */
const buildContexteEnonce = (base, withConclusion = true) => {
    const { intro, conclusion, type, criteres, candidats } = base;
    const listCriteres = criteres.map((c) => `<li>${c.phrase(c.bornes)}</li>`).join('');
    const ths = criteres.map((c) => `<th>${c.key}</th>`).join('');
    const rows = candidats.map((cd) => {
        const tds = criteres.map((c) => {
            const v = cd.valeurs[c.key];
            const formatted = c.decimals === 0 ? String(v) : toFrFixed(v, c.decimals);
            return `<td>${formatted} ${c.unit}</td>`;
        }).join('');
        return `<tr><td><strong>${cd.nom}</strong></td>${tds}</tr>`;
    }).join('');

    const table = `<table class="tanoed-data-table"><thead><tr><th>${type.singulier.charAt(0).toUpperCase() + type.singulier.slice(1)}</th>${ths}</tr></thead><tbody>${rows}</tbody></table>`;
    const conclusionHtml = withConclusion ? `<p>${conclusion}</p>` : '';
    return `<p>${intro} :</p><ul>${listCriteres}</ul>${table}${conclusionHtml}`;
};

// ─── Générateur A : QCM unique (1 bonne réponse) ───

const genEncadrerContexteQcm = () => {
    const base = genContexteSelection({ nbCandidats: 6, nbValides: 1 });
    const texte = buildContexteEnonce(base, true);
    const correct = base.candidats.find((c) => c.valid).nom;
    const choix = base.candidats.map((c) => c.nom);
    return makeQcm(choix, correct, texte, opts(L_CONTEXTE));
};

// ─── Générateur B : Tableau Vrai/Faux ───

const genEncadrerContexteVF = () => {
    const base = genContexteSelection({ nbCandidats: 6, nbValides: 3 });
    const texte = buildContexteEnonce(base, false);
    // Chaque ligne = "X convient." — Vrai si X respecte tous les critères
    const lignes = base.candidats.map((c) => `${c.nom} convient.`);
    const bonnesReponses = base.candidats.map((c) => (c.valid ? 0 : 1));
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CONTEXTE));
};

// ─── Générateur C : Classer (drag-and-drop) ───

const genEncadrerContexteClasser = () => {
    const base = genContexteSelection({ nbCandidats: 6, nbValides: 2 });
    const texte = buildContexteEnonce(base, false);
    const valides = base.candidats.filter((c) => c.valid).map((c) => c.nom);
    const nonValides = base.candidats.filter((c) => !c.valid).map((c) => c.nom);
    const categories = [
        { name: 'Convient', items: valides },
        { name: 'Ne convient pas', items: nonValides },
    ];
    return makeClasser(categories, texte, opts(L_CONTEXTE));
};

// ─── Registre ───
const GENERATORS = {
    encadrer_qcm_unique: genEncadrerQcm,
    encadrer_qcm_multiple: genEncadrerQcmMultiple,
    encadrer_relier: genEncadrerRelier,
    encadrer_tableau_vf: genEncadrerVF,
    encadrer_texte_trou_drag: genEncadrerTrouDrag,
    encadrer_contexte_qcm_unique: genEncadrerContexteQcm,
    encadrer_contexte_tableau_vf: genEncadrerContexteVF,
    encadrer_contexte_classer: genEncadrerContexteClasser,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix, exclude = null) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`) && (!exclude || !k.startsWith(`${exclude}_`)))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'encadrer', label: 'Encadrer et intercaler un décimal', formatsList: buildFormats('encadrer', 'encadrer_contexte') },
    { id: 'encadrer_contexte', label: 'Encadrer — problème concret (contexte réel)', formatsList: buildFormats('encadrer_contexte') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
